import { useEffect } from 'react'
import type { SpreadBackgroundPlacements } from '@/lib/backgrounds/types/spread-background-placements'
import { applyBackgroundPlacement } from '@/lib/backgrounds/background-placement'
import type { SpreadPageRect } from '@/lib/backgrounds/types/spread-page-rect'
import type { SpreadPageRects } from '@/lib/backgrounds/types/spread-page-rects'
import {
  backgroundBleedRect,
  backgroundImageRect,
  unionSpreadPageRect,
} from '@/lib/backgrounds/spread-page-rects'
import type { BackgroundDropSlot } from '@/lib/backgrounds/types/background-drop-slot'
import { resolveBackgroundDropSlot } from '@/lib/backgrounds/resolve-background-drop-slot'
import { DEFAULT_BACKGROUND_TINT } from '@/lib/backgrounds/saved-spread-backgrounds'
import {
  type BackgroundPlacementCanvasApi,
  useBackgroundPlacementOptional,
} from '@/contexts/background-placement-context'
import { useSpreadPageRects } from '../hooks/use-spread-page-rects'
import { BackgroundFillRect } from './components/background-fill-rect'
import type { SpreadBackgroundGeometry } from './types/spread-background-geometry'

function slotRect(slot: BackgroundDropSlot, pageRects: SpreadPageRects): SpreadPageRect | null {
  return slot === 'double' ? unionSpreadPageRect(pageRects) : pageRects[slot]
}

/**
 * Placed backgrounds, plus a live preview of the background being dragged in the slot it would
 * land in. Rendered INSIDE the document's `BleedFrame` (via `DoubleSpreadDocument`/
 * `SinglePageDocument`'s `pageBackground` slot) right after `.doc-page-fill`, so it paints above
 * the paper but below bleed tints, grids, guides and all artwork — the same stacking legacy's
 * `backgroundlayer` has. Coordinates are canvas pts (`documentCanvasRef` space). Each background
 * fills its slot's bleed box (`backgroundBleedRect`), not just the trim.
 *
 * The preview goes through `applyBackgroundPlacement`, so it hides exactly what the drop would
 * replace: hovering a page hides a spread background, hovering the spread hides both pages.
 */
export function SpreadBackgroundFills(geometry: SpreadBackgroundGeometry) {
  const placement = useBackgroundPlacementOptional()
  const pageRects = useSpreadPageRects(geometry)

  if (!placement) return null

  const { hoverSlot, isDraggingBackground, draggingBackground, placedBackgrounds } = placement
  const previewSlot = isDraggingBackground && draggingBackground ? hoverSlot : null

  const visible: SpreadBackgroundPlacements =
    previewSlot && draggingBackground
      ? applyBackgroundPlacement(placedBackgrounds, previewSlot, {
          background: draggingBackground,
          tint: DEFAULT_BACKGROUND_TINT,
        })
      : placedBackgrounds

  return (
    <>
      {(['double', 'left', 'right'] as const).map((slot) => {
        const placed = visible[slot]
        const rect = placed ? backgroundBleedRect(slot, pageRects, geometry.bleedMargin) : null
        const imageRect = placed ? backgroundImageRect(slot, pageRects, geometry.bleedMargin) : null

        return placed && rect ? (
          <BackgroundFillRect
            key={slot}
            rect={rect}
            imageRect={imageRect ?? rect}
            placed={placed}
            isPreview={slot === previewSlot}
          />
        ) : null
      })}
    </>
  )
}

type SpreadBackgroundLayerProps = SpreadBackgroundGeometry & {
  toCanvasPoint: (clientX: number, clientY: number) => { canvasX: number; canvasY: number } | null
  isOverCanvas: (clientX: number, clientY: number) => boolean
}

/**
 * Mounted directly in `documentCanvasRef` (by `center-canvas/components/canvas-drop-targets.tsx`),
 * above the artwork. Two jobs:
 *
 * 1. The canvas-side "bridge" (mirrors `PhotoPlacementCanvasBridge`): registers a
 *    `BackgroundPlacementCanvasApi` so the context's pointer handlers can resolve which page (if
 *    any) the cursor is over, without knowing any canvas geometry themselves.
 * 2. The drop-target highlight. Legacy draws a flat 50% black box on its selection layer
 *    (`BackgroundSection`, page-editor's `odeditorsvg.js` ~line 4029); here the image preview
 *    itself renders under the artwork (`SpreadBackgroundFills`), so this only outlines the target
 *    area — kept above the artwork so the target stays obvious on a page full of photos.
 */
export function SpreadBackgroundLayer({
  toCanvasPoint,
  isOverCanvas,
  ...geometry
}: SpreadBackgroundLayerProps) {
  const placement = useBackgroundPlacementOptional()
  const pageRects = useSpreadPageRects(geometry)

  useEffect(() => {
    if (!placement) return

    const api: BackgroundPlacementCanvasApi = {
      isOverCanvas,
      resolveDropSlot: (clientX, clientY, isDoublePageBackground) => {
        const pt = toCanvasPoint(clientX, clientY)

        if (!pt) return null

        return resolveBackgroundDropSlot(pt.canvasX, pt.canvasY, pageRects, isDoublePageBackground)
      },
      availableSides: { left: pageRects.left != null, right: pageRects.right != null },
    }

    placement.registerCanvasApi(api)

    return () => placement.registerCanvasApi(null)
  }, [placement, isOverCanvas, toCanvasPoint, pageRects])

  if (!placement) return null

  const { hoverSlot, isDraggingBackground } = placement
  const hoverRect = isDraggingBackground && hoverSlot ? slotRect(hoverSlot, pageRects) : null

  if (!hoverRect) return null

  return (
    <div className="pointer-events-none absolute inset-0" style={{ zIndex: 30 }}>
      <div
        className="absolute border-2 border-primary bg-primary/10"
        style={{
          left: hoverRect.x,
          top: hoverRect.y,
          width: hoverRect.width,
          height: hoverRect.height,
        }}
        data-background-drop-target={hoverSlot}
      />
    </div>
  )
}
